import React, { useEffect, useState, useRef, CSSProperties, useContext } from "react";
import { Camera, Pencil } from "lucide-react";
import { fetchFile } from "hooks/file";
import { updateProfileImage } from "hooks/profile";
import FileInterface from "types/file";
import { AuthContext, IntlContext } from "context";
import blobToBase64 from "helpers/file/blobToBase64";
import { ACCEPTED_IMAGE_MIMES, PROFILE_PHOTO_MAX_BYTES, PROFILE_PHOTO_MAX_MEGABYTES } from "helpers/file/uploadConstraints";
import { cn } from "lib/utils";
import { toast } from "sonner";

const ImageUpload: React.FC<{
    id: string | undefined | null;
    defaultValue?: string;
    alt?: string;
    height?: string;
    setWidthImage?: (width: number) => void;
    setHeightImage?: (height: number) => void;
    setShowImageDefault?: (show: boolean) => void;
    withUpload?: boolean;
    variant?: "default" | "avatar";
}> = ({
    id,
    defaultValue,
    alt,
    height,
    setWidthImage,
    setHeightImage,
    setShowImageDefault,
    withUpload = true,
    variant = "default",
}) => {
    const intl = useContext(IntlContext);
    const [image, setImage] = useState<FileInterface>({
        id: "",
        size: 0,
        content: new Blob(),
        name: "",
        type: "",
    });
    const { account, updateAccount } = useContext(AuthContext);
    const ref = useRef<HTMLImageElement | null>(null);
    const refInputImage = useRef<HTMLInputElement | null>(null);
    const imageId = image.id;
    let style: CSSProperties = {};

    if (height) {
        style.height = height;
    }

    const onClickEditImage = () => {
        refInputImage.current?.click();
    };

    const onChangeImage = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
        let blobFile: { name: string; size: number; type: string; content: File | string } | null = null;
        if (target.files) {
            const file = target.files.item(0);
            if (file) {
                if (file.size > PROFILE_PHOTO_MAX_BYTES) {
                    toast.error(intl.formatMessage("generic.upload.too_large", { max: PROFILE_PHOTO_MAX_MEGABYTES }));
                    target.value = "";
                    return;
                }
                blobFile = Object.assign({ name: file.name, size: file.size, type: file.type }, { content: file });
            }
        }

        if (blobFile?.content && blobFile.content instanceof Blob) {
            blobFile.content = await blobToBase64(blobFile.content);
        }

        const data = {
            picture: blobFile,
        };

        try {
            await updateProfileImage(account!.user.id, data);
        } catch {
            toast.error(intl.formatMessage("generic.upload.failed"));
            target.value = "";
            return;
        }
        toast.success(intl.formatMessage("profile.user.data.update"));
        updateAccount();
    };

    useEffect(() => {
        if (!id || imageId === id) {
            setShowImageDefault && setShowImageDefault(true);
            return;
        }

        const promise = fetchFile(id);
        promise
            .then((image) => {
                setImage(image);
            })
            .catch((_e) => {
                setShowImageDefault && setShowImageDefault(true);
            });

        return () => {
            promise.cancel();
        };
    }, [id, ref, imageId, setShowImageDefault]);

    useEffect(() => {
        if (!id) {
            setShowImageDefault && setShowImageDefault(true);
        }
    }, [id, setShowImageDefault]);

    const isAvatar = variant === "avatar";
    const hasPicture = Boolean(id && image.id !== "");
    const initials = defaultValue ?? `${account?.user.first_name.charAt(0) ?? ""}${account?.user.last_name.charAt(0) ?? ""}`;

    return (
        <div className={cn("relative", isAvatar && "h-full w-full")}>
            <div
                className={cn(
                    "overflow-hidden bg-gradient-to-br from-indigo-200 to-purple-200",
                    isAvatar ? "h-full w-full rounded-2xl" : "rounded-2xl"
                )}
                style={!isAvatar && height ? { height } : undefined}
            >
                {hasPicture ? (
                    <img
                        className={cn("block h-full w-full object-cover", !isAvatar && !height && "max-h-40")}
                        src={URL.createObjectURL(image.content)}
                        alt={alt || image.name || ""}
                        ref={ref}
                        onLoad={() => {
                            if (!ref.current) return;
                            setWidthImage?.(ref.current.naturalWidth);
                            setHeightImage?.(ref.current.naturalHeight);
                        }}
                    />
                ) : (
                    <div
                        className={cn(
                            "flex h-full w-full items-center justify-center",
                            isAvatar ? "min-h-28" : "min-h-32 px-6 py-8"
                        )}
                    >
                        <span className="text-2xl font-bold tracking-wide text-white/90">{initials}</span>
                    </div>
                )}
            </div>
            {withUpload && (
                <button
                    type="button"
                    className={cn(
                        "absolute flex items-center justify-center bg-white shadow-md transition-colors hover:bg-gray-50",
                        isAvatar
                            ? "bottom-1 right-1 h-9 w-9 rounded-xl border border-gray-100"
                            : "bottom-2 right-2 h-10 w-10 rounded-full border border-gray-200"
                    )}
                    onClick={onClickEditImage}
                    aria-label={hasPicture ? "Change profile photo" : "Add profile photo"}
                >
                    <input
                        type="file"
                        accept={ACCEPTED_IMAGE_MIMES}
                        ref={refInputImage}
                        className="sr-only"
                        onChange={onChangeImage}
                    />
                    {isAvatar || !hasPicture ? (
                        <Camera className="h-4 w-4 text-primary" strokeWidth={2.25} />
                    ) : (
                        <Pencil className="h-4 w-4 text-primary" strokeWidth={2.25} />
                    )}
                </button>
            )}
        </div>
    );
};

export default ImageUpload;
